import type { ReactNode } from 'react';
import { cx } from '../cx';

export function Tabs({ children }: { children: ReactNode }) {
  return (
    <div className="rr-tabs" role="tablist">
      {children}
    </div>
  );
}

export function Tab({
  label,
  sub,
  selected,
  onClick,
}: {
  label: string;
  sub?: string;
  selected?: boolean;
  onClick?: () => void;
}) {
  return (
    <button className="rr-tab" role="tab" aria-selected={selected} onClick={onClick} type="button">
      <span>{label}</span>
      {sub && <span className="rr-tab__sub">{sub}</span>}
    </button>
  );
}

export function Index({ children }: { children: ReactNode }) {
  return <nav className="rr-index">{children}</nav>;
}

export function IndexItem({
  label,
  count,
  current,
  onClick,
}: {
  label: string;
  count?: number | string;
  current?: boolean;
  onClick?: () => void;
}) {
  return (
    <button className="rr-index__item" aria-current={current || undefined} onClick={onClick} type="button">
      <span>{label}</span>
      {count !== undefined && <span className="rr-index__count">{count}</span>}
    </button>
  );
}

export function Breadcrumbs({ items }: { items: { label: string; href?: string; render?: (label: string) => ReactNode }[] }) {
  return (
    <div className="rr-crumbs">
      {items.map((item, i) => {
        const last = i === items.length - 1;
        return (
          <span key={`${item.label}-${i}`} className={cx(last && 'rr-crumbs__current')} style={{ display: 'contents' }}>
            {i > 0 && <span className="rr-crumbs__sep">/</span>}
            {item.render ? item.render(item.label) : <span className={cx(last && 'rr-crumbs__current')}>{item.label}</span>}
          </span>
        );
      })}
    </div>
  );
}

/** Etapas. Com `onStep`, cada etapa vira um botão que abre a seção correspondente. */
export function Stepper({ steps, current, onStep, hints }: { steps: string[]; current: number; onStep?: (index: number) => void; hints?: string[] }) {
  return (
    <div className="rr-stepper">
      {steps.map((s, i) => (
        <span key={s} style={{ display: 'contents' }}>
          {i > 0 && <span className="rr-stepper__line" />}
          {onStep ? (
            <button type="button" className="rr-stepper__step rr-stepper__step--click" aria-current={i === current ? 'step' : undefined} onClick={() => onStep(i)} title={hints?.[i]}>
              <span className={cx('rr-dot', i === current ? 'rr-dot--active' : i < current ? '' : 'rr-dot--hollow')} />
              {s}
            </button>
          ) : (
            <span className="rr-stepper__step" aria-current={i === current ? 'step' : undefined}>
              <span className={cx('rr-dot', i === current ? 'rr-dot--active' : i < current ? '' : 'rr-dot--hollow')} />
              {s}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

export function Avatar({ name, accent }: { name: string; accent?: boolean }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  return (
    <span className={cx('rr-avatar', accent && 'rr-avatar--accent')} title={name}>
      {initials || '?'}
    </span>
  );
}
